import React, {
  ComponentType,
  useState
} from 'react'
import {
  Accordion,
  Button,
  Grid,
  Popup
} from 'semantic-ui-react'
//  Local
import {
  CONDITION,
  NOTE,
  ROLES,
  shouldRenderReskin2025
} from '../../../constant'
import {
  conditionClaimSource,
  condition as conditionUtils
} from '../../common/conditionUtils'
import {
  formatConditionTitle,
  displayRiskCode
} from '../../common/formatUtils'
import AddressedIcon from '../common/addressedIcon'
import ConditionDismissed from '../common/conditionDismissed'
import CdiConditionHeaderToolbar from './cdiConditionHeaderToolbar'
import ConditionNotes from '../common/ConditionNotes'
import CdiConditionDetail from './cdiConditionDetail'
import CdiConditionEditor from './cdiConditionEditor'
import CdiPotentialConditionTagsEditor from './cdiPotentialConditionTagsEditor'
import NLPExtractedLink from '../common/nlpExtractedLink'
import {
  EncounterConditionDisplay,
  User
} from '../../authenticated/types'
import If from '../../common/if'
import ReveleerButton from '../../common/ReveleerButton'
import { ObjectValues } from '../../../types/types'

const SafeGrid = Grid as unknown as ComponentType<any>

type Props = {
  condition: EncounterConditionDisplay
  encounterDate: number
  currentUser: User
  displayIndex: number
  hccChild: boolean
  isActive: boolean
  onClickTitle: (
    condition: EncounterConditionDisplay
  ) => void
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: EncounterConditionDisplay[] | EncounterConditionDisplay | string
  ) => void,
  onSubmitNote: (
    condition: EncounterConditionDisplay,
    noteBody: string,
    noteReason: ObjectValues<typeof NOTE.REASON>
  ) => void
}

function CdiConditionEdit(props: Props) {
  const {
    condition,
    encounterDate,
    currentUser,
    displayIndex,
    hccChild: isHCCChild,
    isActive,
    onClickTitle,
    onConditionAction,
    onSubmitNote
  } = props
  const [
    isEditModeState,
    setIsEditModeState
  ] = useState<boolean>(false)
  const [
    conditionState,
    setConditionState
  ] = useState<EncounterConditionDisplay>(condition)

  const onSubmitNoteFn = (
    conditionProp: EncounterConditionDisplay,
    noteBody: string,
    noteReason: ObjectValues<typeof NOTE.REASON>
  ): void => {
    onSubmitNote(
      conditionProp,
      noteBody,
      noteReason
    )
  }

  const onClickEdit = (): void => {
    setIsEditModeState(true)
  }

  const onClickCancel = (): void => {
    setIsEditModeState(false)
  }

  const onSaveCondition = (
    conditionProp: EncounterConditionDisplay
  ): void => {
    onConditionAction(
      CONDITION.ACTION.UPDATE_WITH_RULES,
      conditionProp
    )
    setConditionState(conditionProp)
    setIsEditModeState(false)
  }

  const claimSource = conditionClaimSource(conditionState)
  const hccCode = conditionState.hccCode
    ? `(${displayRiskCode(conditionState.hccCode)})`
    : ''
  const isHccChild = isHCCChild === true
  const titleText = formatConditionTitle(condition)
  const displayClassName = displayIndex && displayIndex === 0
  const reskinClassName = shouldRenderReskin2025
    ? ' reskin'
    : ''
  const clazz = `${displayClassName}${reskinClassName}`
    ? 'flex-title first-accordion hover' : 'flex-title hover'
  const isReadMode = !isEditModeState
  const containerId = `cdi-encounter-condition-edit-${condition.id}`
  const titleId = `cdi-encounter-condition-edit-title-${condition.id}`
  const contentId = `cdi-encounter-condition-edit-body-${condition.id}`
  const editButtonId = `cdi-encounter-condition-edit-button-${condition.id}`

  const conditionTitle = shouldRenderReskin2025
    ? 'condition-title reskin'
    : 'condition-title'

  return (
    <div
      id={ containerId }
      className="condition">
      <Accordion.Title
        active={ isActive }
        index={ conditionState.id }
        onClick={ () => { if (!isHccChild) onClickTitle(conditionState) } }
        className={ clazz }>
        <AddressedIcon condition={ conditionState } />
        <div
          id={ titleId }
          className={ conditionTitle }
        >
          <If isTrue={ isActive }>
            <strong>
              { `${hccCode} ${titleText}` }
            </strong>
          </If>
          <If isTrue={ !isActive }>
            <span>
              <strong>
                { hccCode }
              </strong>
              &nbsp;{ titleText }
            </span>
          </If>
        </div>
        <div className="condition-toolbar">
          <div className="hover__no-hover">
            <ConditionDismissed condition={ conditionState } />
          </div>
          <div className="hover__hover">
            <CdiConditionHeaderToolbar
              condition={ conditionState }
              onClick={ onConditionAction } />
          </div>
        </div>
      </Accordion.Title>
      <Accordion.Content active={ isActive }>
        <SafeGrid
          className="internal-condition"
          columns={ 1 }
          divided
          stackable
        >
          <Grid.Row className="first-row">
            <Grid.Column>
              <div id={ contentId }>
                <If isTrue={ isEditModeState }>
                  <CdiConditionEditor
                    condition={ conditionState }
                    encounterDate={ encounterDate }
                    onCancel={ onClickCancel }
                    onSave={ onSaveCondition }
                  />
                </If>
                <If isTrue={ isReadMode }>
                  <CdiConditionDetail condition={ conditionState } />
                </If>
                <NLPExtractedLink
                  condition={ conditionState }
                  currentUser={ currentUser }
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <If isTrue={ !!claimSource }>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <span className="footnote">
                  Initial source of condition: { claimSource }
                </span>
              </Grid.Column>
            </Grid.Row>
          </If>
          <If isTrue={ isReadMode && conditionUtils.isPotential(conditionState) }>
            <Grid.Row className="divider">
              <Grid.Column>
                <CdiPotentialConditionTagsEditor
                  condition={ conditionState }
                  onConditionAction={ onConditionAction }
                />
              </Grid.Column>
            </Grid.Row>
          </If>
          <If isTrue={ isReadMode }>
            <Grid.Row className="divider">
              <Grid.Column>
                <ConditionNotes
                  condition={ conditionState }
                  userRole={ ROLES.CDI }
                  onSubmit={ onSubmitNoteFn }
                />
              </Grid.Column>
            </Grid.Row>
          </If>
          <If isTrue={ isReadMode }>
            <Grid.Row>
              <Grid.Column textAlign="right">
                <Popup
                  content="Edit This Condition"
                  mouseEnterDelay={ 1000 }
                  trigger={
                    shouldRenderReskin2025
                      ? (
                        <ReveleerButton
                          buttonLabel="Edit"
                          buttonSize="small"
                          buttonType="primary"
                          id={ editButtonId }
                          isDisabled={ false }
                          onClick={ onClickEdit }
                          type="button"
                        />
                      )
                      : (
                        <Button
                          id={ editButtonId }
                          color="blue"
                          onClick={ onClickEdit }
                        >
                          Edit
                        </Button>
                      )
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </If>
        </SafeGrid>
      </Accordion.Content>
    </div>
  )
}

export default CdiConditionEdit
